import { useState } from "react";
import { MapPin, Phone, Clock, Mail, Instagram, Facebook, ExternalLink } from "lucide-react";
import { WhatsAppIcon } from "./WhatsAppIcon";
import { toast } from "sonner";
import { useCookieConsent } from "../hooks/useCookieConsent";

export const ContactSection = ({ businessInfo }) => {
  const consent = useCookieConsent();
  const [mapAllowed, setMapAllowed] = useState(false);
  const showMap = consent === "accepted" || mapAllowed;

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const { name, email, phone, message } = formData;
    const text =
      `Neue Nachricht von der Webseite%0A` +
      `Name: ${name}%0A` +
      `E-Mail: ${email}%0A` +
      `Telefon: ${phone}%0A%0A` +
      `${message}`;
    const base = (businessInfo && businessInfo.social && businessInfo.social.whatsapp) || "https://wa.me/41762167800";
    window.open(`${base}?text=${encodeURIComponent(text.replace(/%0A/g, "\n"))}`, "_blank", "noopener,noreferrer");
    toast.success("WhatsApp wird geöffnet – senden Sie uns Ihre Nachricht!");
    setFormData({ name: "", email: "", phone: "", message: "" });
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  if (!businessInfo) return null;

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-white"
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <p
            className="text-[#C5A059] text-sm tracking-[0.3em] uppercase mb-4"
            data-testid="contact-tagline"
          >
            Kontakt & Standort
          </p>
          <h2
            className="font-playfair text-3xl md:text-4xl lg:text-5xl text-[#1A1A1A] leading-tight mb-6"
            data-testid="contact-title"
          >
            Besuchen Sie <span className="italic">uns</span>
          </h2>
          <div className="w-16 h-[1px] bg-gradient-to-r from-[#C5A059] to-[#3d7d83] mx-auto" />
        </div>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Contact Info & Map */}
          <div>
            {/* Interactive Google Map – loaded only after cookie consent (DSG/DSGVO) */}
            <div
              className="relative overflow-hidden mb-4 border border-[#E5E5E5]"
              data-testid="contact-map"
            >
              {showMap ? (
                <iframe
                  title="Standort Urban Beauty Klosters"
                  src="https://www.google.com/maps?q=Landstrasse%20206%2C%207250%20Klosters%2C%20Switzerland&output=embed"
                  className="w-full h-[300px] border-0"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allowFullScreen
                  data-testid="contact-map-iframe"
                />
              ) : (
                <div
                  className="w-full h-[300px] bg-[#F9F8F6] flex flex-col items-center justify-center text-center px-6"
                  data-testid="contact-map-placeholder"
                >
                  <MapPin className="text-[#C5A059] mb-3" size={28} />
                  <p className="text-[#666666] text-sm max-w-sm mb-5 leading-relaxed">
                    Zur Anzeige der Karte werden Inhalte von Google Maps geladen.
                    Dabei können Daten an Google übertragen werden.
                  </p>
                  <button
                    type="button"
                    onClick={() => setMapAllowed(true)}
                    className="bg-[#1A1A1A] text-white px-6 py-3 rounded-full text-sm tracking-wider uppercase font-medium hover:bg-[#C5A059] hover:text-[#1A1A1A] transition-colors duration-300"
                    data-testid="contact-map-load-btn"
                  >
                    Karte anzeigen
                  </button>
                </div>
              )}
            </div>
            <a
              href={businessInfo.google_maps_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm tracking-wider uppercase text-[#1A1A1A] hover:text-[#C5A059] transition-colors mb-8"
              data-testid="contact-map-link"
            >
              <ExternalLink size={16} />
              Route planen
            </a>

            {/* Info Cards */}
            <div className="grid sm:grid-cols-2 gap-4">
              {/* Address */}
              <div
                className="bg-[#F9F8F6] p-6"
                data-testid="contact-address-card"
              >
                <MapPin className="text-[#C5A059] mb-3" size={24} />
                <h3 className="font-medium text-[#1A1A1A] mb-2">Adresse</h3>
                <p className="text-[#666666] text-sm leading-relaxed">
                  {businessInfo.address}
                </p>
              </div>

              {/* Phone */}
              <div
                className="bg-[#F9F8F6] p-6"
                data-testid="contact-phone-card"
              >
                <Phone className="text-[#C5A059] mb-3" size={24} />
                <h3 className="font-medium text-[#1A1A1A] mb-2">Telefon</h3>
                <a
                  href={`tel:${businessInfo.phone.replace(/\s/g, "")}`}
                  className="text-[#666666] text-sm hover:text-[#C5A059] transition-colors"
                >
                  {businessInfo.phone}
                </a>
              </div>

              {/* Opening Hours */}
              <div
                className="bg-[#F9F8F6] p-6 sm:col-span-2"
                data-testid="contact-hours-card"
              >
                <Clock className="text-[#C5A059] mb-3" size={24} />
                <h3 className="font-medium text-[#1A1A1A] mb-4">Öffnungszeiten</h3>
                <div className="grid grid-cols-1 gap-y-2 text-sm">
                  {Object.entries(businessInfo.opening_hours).map(([day, hours]) => (
                    <div key={day} className="flex justify-between">
                      <span className="text-[#666666]">{day}</span>
                      <span className="text-[#1A1A1A]">{hours}</span>
                    </div>
                  ))}
                </div>
                <p
                  className="text-sm text-[#1A1A1A] font-medium mt-4 pt-4 border-t border-[#E5E5E5] flex items-center gap-2"
                  data-testid="contact-hours-note"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-[#C5A059]" aria-hidden="true" />
                  Behandlungen nur nach Terminvereinbarung.
                </p>
              </div>
            </div>

            {/* Social Links */}
            <div className="flex gap-4 mt-6" data-testid="contact-social-links">
              <a
                href={businessInfo.social.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="w-12 h-12 rounded-full border border-[#E5E5E5] flex items-center justify-center text-[#1A1A1A] hover:bg-[#E1306C] hover:text-white hover:border-[#E1306C] transition-all duration-300"
                aria-label="Instagram"
                data-testid="social-instagram"
              >
                <Instagram size={20} />
              </a>
              <a
                href={businessInfo.social.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="w-12 h-12 rounded-full border border-[#E5E5E5] flex items-center justify-center text-[#1A1A1A] hover:bg-[#1877F2] hover:text-white hover:border-[#1877F2] transition-all duration-300"
                aria-label="Facebook"
                data-testid="social-facebook"
              >
                <Facebook size={20} />
              </a>
              {businessInfo.social.whatsapp && (
                <a
                  href={businessInfo.social.whatsapp}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 rounded-full border border-[#E5E5E5] flex items-center justify-center text-[#1A1A1A] hover:bg-[#25D366] hover:text-white hover:border-[#25D366] transition-all duration-300"
                  aria-label="WhatsApp"
                  data-testid="social-whatsapp"
                >
                  <WhatsAppIcon size={20} />
                </a>
              )}
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-[#F9F8F6] p-8 md:p-12">
            <h3
              className="font-playfair text-2xl text-[#1A1A1A] mb-2"
              data-testid="contact-form-title"
            >
              Schreiben Sie uns
            </h3>
            <p className="text-[#666666] mb-8">
              Haben Sie Fragen? Wir freuen uns auf Ihre Nachricht.
            </p>

            <form onSubmit={handleSubmit} data-testid="contact-form">
              <div className="space-y-6">
                <div>
                  <label
                    htmlFor="name"
                    className="block text-sm font-medium text-[#1A1A1A] mb-2"
                  >
                    Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-white border border-[#E5E5E5] focus:border-[#C5A059] focus:outline-none transition-colors"
                    placeholder="Ihr Name"
                    data-testid="contact-input-name"
                  />
                </div>

                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-[#1A1A1A] mb-2"
                  >
                    E-Mail *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-white border border-[#E5E5E5] focus:border-[#C5A059] focus:outline-none transition-colors"
                    placeholder="ihre@email.ch"
                    data-testid="contact-input-email"
                  />
                </div>

                <div>
                  <label
                    htmlFor="phone"
                    className="block text-sm font-medium text-[#1A1A1A] mb-2"
                  >
                    Telefon
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-white border border-[#E5E5E5] focus:border-[#C5A059] focus:outline-none transition-colors"
                    placeholder="+41 76 000 00 00"
                    data-testid="contact-input-phone"
                  />
                </div>

                <div>
                  <label
                    htmlFor="message"
                    className="block text-sm font-medium text-[#1A1A1A] mb-2"
                  >
                    Nachricht *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={4}
                    className="w-full px-4 py-3 bg-white border border-[#E5E5E5] focus:border-[#C5A059] focus:outline-none transition-colors resize-none"
                    placeholder="Ihre Nachricht..."
                    data-testid="contact-input-message"
                  />
                </div>

                <button
                  type="submit"
                  className="w-full btn-primary"
                  data-testid="contact-submit-btn"
                >
                  Über WhatsApp senden
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};
